import { FC } from 'react';
import { useState, useRef, useEffect } from 'react';
import { PinturaEditor } from '@pqina/react-pintura';
import {
  colorStringToColorArray,
  PinturaImageState,
  PinturaDefaultImageWriterResult,
  // degToRad,
} from '@pqina/pintura';
// pintura
import '@pqina/pintura/pintura.css';
import {
  // editor
  createDefaultImageReader,
  createDefaultImageWriter,
  createDefaultShapePreprocessor,

  // plugins
  setPlugins,
  plugin_crop,
} from '@pqina/pintura';

import { LocaleCore, LocaleCrop } from '@pqina/pintura/locale/en_GB';

import Logo from '/creme_logo.png';
import { ImageEditorStylesWrapper } from './ImageEditorStylesWrapper';
import { useEditorStore } from './EditorStore';

setPlugins(plugin_crop);

const editorDefaults = {
  utils: ['crop'],
  // layoutVerticalControlTabsPreference: 'above',
  imageReader: createDefaultImageReader(),
  imageWriter: createDefaultImageWriter(),
  shapePreprocessor: createDefaultShapePreprocessor(),
  resizeAspectRatioLocked: false,
  locale: {
    ...LocaleCore,
    ...LocaleCrop,
  },
};

type Props = {
  imageFile: File;
  onUpdate?: (data: PinturaImageState) => void;
  onProcess?: (data: PinturaDefaultImageWriterResult) => void;
  /**
   * Called when Pintura fails to load the image source (e.g. the File
   * became unreadable on the device). Without a handler the load error
   * would surface as an unhandled rejection.
   */
  onLoadError?: (error: unknown) => void;
};

export const ImageEditor: FC<Props> = ({
  imageFile,
  onUpdate = () => {},
  onProcess = () => {},
  onLoadError = () => {},
}) => {
  const {
    zoomInputEnabled,
    rotationInputEnabled,
    imageFlipX,
    setEditor,
    cropAreaSize,
    setCropAreaSize,
    cropAreaCoordinates,
    setCropAreaCoordinates,
  } = useEditorStore();
  const editorRef = useRef<PinturaEditor | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (editorRef && editorRef.current) {
      setEditor(editorRef.current.editor);
    }
  }, [setEditor, editorRef]);

  return (
    <ImageEditorStylesWrapper>
      <PinturaEditor
        ref={editorRef}
        {...editorDefaults}
        src={imageFile}
        imageCropAspectRatio={1}
        previewUpscale={true}
        imageCropLimitToImage={true}
        // imageCropMinSize={{ width: 500, height: 500 }}
        // imageRotation={degToRad(45)}
        cropEnableZoomInput={zoomInputEnabled}
        cropEnableRotationInput={rotationInputEnabled}
        imageFlipX={imageFlipX}
        cropEnableImageSelection={false}
        cropEnableZoomAutoHide={false}
        cropEnableButtonRotateLeft={false}
        cropEnableButtonFlipHorizontal={false}
        enableToolbar={false}
        enableUtils={false}
        // imageFrame={{
        //   // the key of the frame in the menu
        //   id: 'polaroid',

        //   // the style of the frame
        //   frameStyle: 'polaroid',

        //   // current style properties
        //   frameColor: [1, 1, 1],
        // }}
        imageDecoration={
          [
            // a red square
            // {
            //   x: 100,
            //   y: 100,
            //   width: 128,
            //   height: 128,
            //   backgroundColor: [1, 0, 0],
            // },
          ]
        }
        willRenderToolbar={(nodeList) => {
          // Replace, remove, update, or add nodes to `nodeList` here, then return when done
          return nodeList;
        }}
        // willRenderUtilTabs={willRenderUtilTabs}
        willRenderCanvas={(shapes, state) => {
          const { utilVisibility, selectionRect } = state;

          // Exit if crop utils is not visible
          // if (utilVisibility.crop <= 0) return shapes;

          // Get variable shortcuts to the crop selection rect
          const { x, y, width, height } = selectionRect;

          // Logo & Image Overlays ---------------------------------------------
          const imageWidth = 929;
          const imageHeight = 505;

          // // Define padding as a percentage of the smaller dimension (width or height)
          const paddingPercent = 0.25; // 5% padding
          const padding = Math.min(width, height) * paddingPercent; // Padding scales based on the smaller side

          // // Adjusted dimensions of the rectangle, accounting for padding
          const availableWidth = width - padding * 2;
          const availableHeight = height - padding * 2;

          // The logo is not 100% centered vertically, so we add some extra padding
          // to the top of the image to center it visually
          const extraTopPaddingPercent = 0.02;
          const extraTopPadding = height * extraTopPaddingPercent;

          // The logo is not 100% centered vertically, so we add some extra padding
          // to the top of the image to center it visually
          const extraLeftPaddingPercent = 0.02;
          const extraLeftPadding = width * extraLeftPaddingPercent;

          // // Aspect ratios
          const rectAspectRatio = availableWidth / availableHeight;
          const imageAspectRatio = imageWidth / imageHeight;

          // // Variables to hold the new dimensions of the image
          let newImageWidth, newImageHeight;

          // // Scale the image to fit within the available space (with padding)
          if (imageAspectRatio > rectAspectRatio) {
            // Scale by width
            newImageWidth = availableWidth;
            newImageHeight = availableWidth / imageAspectRatio;
          } else {
            // Scale by height
            newImageHeight = availableHeight;
            newImageWidth = availableHeight * imageAspectRatio;
          }

          // // Calculate the position to center the image in the rectangle (considering padding)
          const offsetX =
            x +
            padding +
            (availableWidth - newImageWidth) / 2 +
            extraLeftPadding;
          const offsetY =
            y +
            padding +
            (availableHeight - newImageHeight) / 2 +
            extraTopPadding;

          // console.log('width', width);
          // console.log('height', height);
          if (cropAreaCoordinates.x !== x || cropAreaCoordinates.y !== y) {
            setCropAreaCoordinates({ x: x, y: y });
          }

          if (cropAreaSize.width !== width || cropAreaSize.height !== height) {
            setCropAreaSize({ width: width, height: height });
          }

          // -------------------------------------------------------------------

          return {
            // Copy all props from current shapes
            ...shapes,

            // Now we add an inverted ellipse shape to the interface shapes array which will be our mask
            interfaceShapes: [
              {
                x: x + width * 0.5,
                y: y + height * 0.5,
                rx: width * 0.5,
                ry: height * 0.5,
                opacity: utilVisibility.crop,
                inverted: true,
                backgroundColor: colorStringToColorArray(
                  'rgba(236, 241, 252, .86)'
                  // 'rgba(255, 255, 255, .7)'
                ),
                strokeWidth: 1,
                strokeColor: colorStringToColorArray('#f3f3f3'), //[...lineColor],
              },
              {
                x: offsetX,
                y: offsetY,
                width: newImageWidth,
                height: newImageHeight,
                backgroundImage: Logo,
              },
              // Spread all existing interface shapes onto the array
              // ...shapes.interfaceShapes,
            ],
          };
        }}
        onLoaderror={(error: unknown) => {
          onLoadError(error);
        }}
        onLoad={() => {
          setImageLoaded(true);
          // if (editorRef.current && imageFile) {
          //   console.log('load image', res);
          //   editorRef.current.editor.history.write(artwork.croppedImageState);
          // }
        }}
        onProcess={(data) => {
          // console.log('history', editorRef.current.editor.history);
          // console.log('process image', dest);
          // setResult(URL.createObjectURL(data.dest));
          onProcess(data);
        }}
        onUpdate={(data) => {
          // console.log('history', editorRef.current.editor.history);
          if (imageLoaded) {
            onUpdate(data);
          }
        }}
      />
    </ImageEditorStylesWrapper>
  );
};
